import { useMemo, type CSSProperties } from 'react';
import type { ItemType, TailwindVersion } from '@/types';

interface RenderedPreviewFrameProps {
  title: string;
  source: string;
  tailwindVersion: TailwindVersion;
  itemType: ItemType;
  titleSuffix?: string;
  className?: string;
  style?: CSSProperties;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function hasFullHtmlDocument(source: string): boolean {
  return /<!doctype\s+html|<html[\s>]/i.test(source);
}

function hasTailwindRuntime(source: string): boolean {
  return /cdn\.tailwindcss\.com|@tailwindcss\/browser/i.test(source);
}

function getTailwindRuntime(version: TailwindVersion): string {
  switch (version) {
    case 'v3':
      return '<script src="https://cdn.tailwindcss.com"></script>';
    case 'none':
      return '';
    case 'v4':
    default:
      return '<script src="https://cdn.jsdelivr.net/npm/@tailwindcss/browser@4"></script>';
  }
}

function buildPreviewHead(version: TailwindVersion): string {
  const runtime = getTailwindRuntime(version);

  return `
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    ${runtime}
    <style>
      html, body {
        margin: 0;
        min-height: 100%;
        background: #ffffff;
        color: #0f172a;
      }

      body {
        font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      }
    </style>
  `.trim();
}

function injectHead(html: string, version: TailwindVersion): string {
  if (hasTailwindRuntime(html)) {
    return html;
  }

  const head = buildPreviewHead(version);

  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${head}\n</head>`);
  }

  if (/<head[\s>]/i.test(html)) {
    return html.replace(/<head([^>]*)>/i, `<head$1>\n${head}`);
  }

  if (/<html[\s>]/i.test(html)) {
    return html.replace(/<html([^>]*)>/i, `<html$1><head>${head}</head>`);
  }

  return `<!doctype html><html lang="en"><head>${head}</head><body>${html}</body></html>`;
}

export function buildPreviewSrcDoc(title: string, source: string, version: TailwindVersion): string {
  const trimmedSource = source.trim();

  if (!trimmedSource) {
    return `<!doctype html>
<html lang="en">
  <head>
    ${buildPreviewHead('none')}
    <style>
      .empty {
        min-height: 100vh;
        display: grid;
        place-items: center;
        padding: 24px;
        text-align: center;
        background: #f8fafc;
      }
      .card {
        max-width: 320px;
        border: 1px dashed #cbd5e1;
        border-radius: 20px;
        background: white;
        padding: 24px;
        box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
      }
      .title {
        margin: 0 0 8px;
        font-size: 18px;
        font-weight: 700;
      }
      .copy {
        margin: 0;
        font-size: 14px;
        line-height: 1.6;
        color: #475569;
      }
    </style>
  </head>
  <body>
    <div class="empty">
      <div class="card">
        <p class="title">${escapeHtml(title)}</p>
        <p class="copy">No preview code has been added yet.</p>
      </div>
    </div>
  </body>
</html>`;
  }

  if (hasFullHtmlDocument(trimmedSource)) {
    return injectHead(trimmedSource, version);
  }

  return `<!doctype html>
<html lang="en">
  <head>
    ${buildPreviewHead(version)}
  </head>
  <body>${trimmedSource}</body>
</html>`;
}

function getHeightClass(itemType: ItemType): string {
  switch (itemType) {
    case 'block':
      return 'h-[540px] sm:h-[630px]';
    case 'section':
      return 'h-[630px] sm:h-[810px]';
    case 'full-page':
      return 'h-[840px] sm:h-[1080px]';
    default:
      return 'h-[630px] sm:h-[780px]';
  }
}

export function RenderedPreviewFrame({
  title,
  source,
  tailwindVersion,
  itemType,
  titleSuffix,
  className = '',
  style,
}: RenderedPreviewFrameProps) {
  const srcDoc = useMemo(() => buildPreviewSrcDoc(title, source, tailwindVersion), [source, tailwindVersion, title]);
  const frameTitle = titleSuffix ? `${title} ${titleSuffix} live preview` : `${title} live preview`;

  return (
    <iframe
      title={frameTitle}
      srcDoc={srcDoc}
      sandbox="allow-scripts"
      loading="lazy"
      className={[getHeightClass(itemType), 'w-full border-0 bg-white', className].join(' ')}
      style={style}
      data-ai-id="item-preview-frame"
    />
  );
}
